import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/lib/auth";
import { queryKeys } from "./keys";

// =============================================================================
// Textes légaux versionnés (A2 + A3, retours Aon du 30/09/2026)
// -----------------------------------------------------------------------------
// Trois kinds portés par `public.legal_texts` (lignes immuables, chaque
// publication = nouvelle version) :
//   - `aon_privacy`    : engagement de confidentialité Aon, acceptation
//                      bloquante côté salarié (fenêtre AonPrivacyGate) ;
//   - `ia_conditions`  : paragraphe « Intelligence artificielle » de la page
//                      Confidentialité (publique) ;
//   - `ia_mention`     : mention courte affichée sous les écrans IA.
//
// `required_version` = plus grande version marquée « à réaccepter » : c'est
// elle qui pilote la fenêtre bloquante, pas la dernière version publiée.
// =============================================================================

export type LegalTextKind = "aon_privacy" | "ia_conditions" | "ia_mention";

export interface LegalTextCurrent {
  id: string;
  kind: LegalTextKind;
  /** Dernière version publiée (texte affiché). */
  version: number;
  /** Plus grande version exigeant une réacceptation (aon_privacy). */
  required_version: number;
  requires_reacceptance: boolean;
  text_fr: string;
  text_en: string | null;
  text_es: string | null;
  published_at: string;
  published_by_label: string | null;
}

export interface LegalTextVersion {
  id: string;
  kind: LegalTextKind;
  version: number;
  text_fr: string;
  text_en: string | null;
  text_es: string | null;
  requires_reacceptance: boolean;
  published_at: string;
  published_by: string | null;
}

/**
 * Règle « le salarié doit accepter » : aucune version acceptée, ou version
 * acceptée inférieure à la version exigeant une réacceptation. Fonction pure,
 * testée par legal-texts.test.ts.
 */
export function aonPrivacyConsentNeeded(
  acceptedVersion: number | null | undefined,
  requiredVersion: number,
): boolean {
  return acceptedVersion == null || acceptedVersion < requiredVersion;
}

/** Choisit la langue du texte affiché, repli sur le français. */
export function pickLegalText(
  text: Pick<LegalTextCurrent, "text_fr" | "text_en" | "text_es"> | null | undefined,
  lang: string,
): string {
  if (!text) return "";
  if (lang.startsWith("en") && text.text_en?.trim()) return text.text_en;
  if (lang.startsWith("es") && text.text_es?.trim()) return text.text_es;
  return text.text_fr;
}

/**
 * Texte en vigueur d'un kind. Les kinds IA sont lisibles en anonyme (page
 * Confidentialité publique) ; `aon_privacy` exige une session.
 */
export function useLegalTextCurrent(kind: LegalTextKind, enabled = true) {
  return useQuery({
    queryKey: ["legal-text", kind] as const,
    enabled,
    staleTime: 60_000,
    queryFn: async (): Promise<LegalTextCurrent | null> => {
      const { data, error } = await supabase.rpc("legal_text_current", { p_kind: kind });
      if (error) throw error;
      return (data as unknown as LegalTextCurrent | null) ?? null;
    },
  });
}

/** Historique des versions d'un kind (carte admin « Versions précédentes »). */
export function useLegalTextVersions(kind: LegalTextKind, enabled = true) {
  return useQuery({
    queryKey: ["legal-text", kind, "versions"] as const,
    enabled,
    queryFn: async (): Promise<LegalTextVersion[]> => {
      const { data, error } = await supabase
        .from("legal_texts")
        .select("*")
        .eq("kind", kind)
        .order("version", { ascending: false });
      if (error) throw error;
      return (data ?? []) as LegalTextVersion[];
    },
  });
}

/** Publication d'une nouvelle version (admin Aon ; version = max+1 côté serveur). */
export function usePublishLegalText() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      kind: LegalTextKind;
      fr: string;
      en?: string | null;
      es?: string | null;
      requiresReacceptance?: boolean;
    }): Promise<LegalTextVersion> => {
      const { data, error } = await supabase.rpc("publish_legal_text", {
        p_kind: input.kind,
        p_fr: input.fr,
        p_en: input.en ?? null,
        p_es: input.es ?? null,
        p_requires_reacceptance: input.requiresReacceptance ?? true,
      });
      if (error) throw error;
      return data as unknown as LegalTextVersion;
    },
    onSuccess: (_row, input) => {
      void queryClient.invalidateQueries({ queryKey: ["legal-text", input.kind] });
      void queryClient.invalidateQueries({ queryKey: ["settings-audit"] });
    },
  });
}

/**
 * Acceptation de l'engagement de confidentialité Aon par le salarié connecté
 * (sa propre fiche). `p_version` doit être une version publiée.
 */
export function useAcceptAonPrivacy() {
  const queryClient = useQueryClient();
  const { effectiveUserId } = useAuth();
  return useMutation({
    mutationFn: async (version: number) => {
      const { data, error } = await supabase.rpc("accept_aon_privacy", {
        p_version: version,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      if (effectiveUserId) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.salarie.mine(effectiveUserId) });
      }
      void queryClient.invalidateQueries({ queryKey: ["legal-text"] });
      void queryClient.invalidateQueries({ queryKey: ["salarie"] });
    },
  });
}
